import _fromPairs from 'lodash/fromPairs';

// actions
import { getUserContext as getUserContextAction } from 'actions/UserActions';

// selectors
import { getFlowData } from 'cards/selectors/singleSampleFlow';

// helpers
import {
  createSubstitutions,
  extractUserWeddingDetails,
  getIsSampleCustomizable,
  matchColorToCardVariation,
} from 'cards/components/common/SingleSample/util/helpers';
import { createAccount, createInvitationsAccount } from 'paper/utils/account';
import { copyUploadcareFileToZola } from 'components/common/UploadcareWidget/utils/api';
import { extractCoupleNames } from 'cards/components/common/SingleSample/NewSingleSampleFlow/utils';
import { trackGoogleTagManagerEvent } from '@zola-helpers/client';
import { STEPS } from 'cards/components/common/SingleSample/NewSingleSampleFlow/constants';
import { AppDispatch, RootState } from 'reducers/index';
import { Customizations, CustomizationsSet } from './reducer';
import ApiService from '../../../util/api';
import { getActiveVariationOptionValues } from '../pdp/selectors';
import { cardSuiteFulfilled } from '../pdp/actions';
import { trackEvent } from '../../../util/trackingHelper';

export const actionTypes = {
  DELETE_UPLOADCARE_IMAGE: 'zola/cards/project/singleSampleFlow/DELETE_UPLOADCARE_IMAGE',
  RESET: 'zola/cards/project/singleSampleFlow/RESET',
  SET_ACCOUNT_DATA: 'zola/cards/project/singleSampleFlow/SET_ACCOUNT_DATA',
  SET_ALL_LP_OR_FOIL: 'zola/cards/project/singleSampleFlow/SET_ALL_LP_OR_FOIL',
  SET_CARD_SUITE_DETAILS: 'zola/cards/project/singleSampleFlow/SET_CARD_SUITE_DETAILS',
  SET_CUSTOMIZATIONS_DATA: 'zola/cards/projects/singleSampleFlow/SET_CUSTOMIZATIONS_DATA',
  SET_CUSTOMIZE_STEP_DATA: 'zola/cards/project/singleSampleFlow/SET_CUSTOMIZE_STEP_DATA',
  SET_DEFAULT_TEMPLATES: 'zola/cards/project/singleSampleFlow/SET_DEFAULT_TEMPLATES',
  SET_ERRORS: 'zola/cards/project/singleSampleFlow/SET_ERRORS',
  SET_HAS_LP_OR_FOIL: 'zola/cards/project/singleSampleFlow/SET_HAS_LP_OR_FOIL',
  SET_NAMES_STEP_DATA: 'zola/cards/project/singleSampleFlow/SET_NAMES_STEP_DATA',
  SET_ORDER_NUMBER: 'zola/cards/project/singleSampleFlow/SET_ORDER_NUMBER',
  SET_SHIPPING_STEP_DATA: 'zola/cards/project/singleSampleFlow/SET_SHIPPING_STEP_DATA',
  SET_SINGLE_SAMPLE_AVAILABLE: 'zola/cards/project/singleSampleFlow/SET_SINGLE_SAMPLE_AVAILABLE',
  SET_STEP_KEY: 'zola/cards/project/singleSampleFlow/SET_STEP_KEY',
  SET_UPLOADCARE_IMAGE: 'zola/cards/project/singleSampleFlow/SET_UPLOADCARE_IMAGE',
  SHIPPING_DATA_FETCHED: 'zola/cards/project/singleSampleFlow/SHIPPING_DATA_FETCHED',
  UPDATE_CUSTOMIZATIONS_DATA: 'zola/cards/projects/singleSampleFlow/UPDATE_CUSTOMIZATIONS_DATA',
  UPDATE_TEMPLATES_SET: 'zola/cards/project/singleSampleFlow/UPDATE_TEMPLATES_SET',
};

export const deleteUploadcareImage = (cardSuiteUUID: any) => ({
  type: actionTypes.DELETE_UPLOADCARE_IMAGE,
  payload: { cardSuiteUUID },
});

export const resetSingleSampleFlow = () => ({
  type: actionTypes.RESET,
});

export const setAccountData = (data: any) => ({
  type: actionTypes.SET_ACCOUNT_DATA,
  payload: { data },
});

const setAllLPOrFoil = () => ({
  type: actionTypes.SET_ALL_LP_OR_FOIL,
});

const setCardSuiteDetails = (suiteDetails: any, activePDPVariation?: any) => ({
  type: actionTypes.SET_CARD_SUITE_DETAILS,
  payload: { suiteDetails, activePDPVariation },
});

export const setCustomizationsData = (data: CustomizationsSet) => ({
  type: actionTypes.SET_CUSTOMIZATIONS_DATA,
  payload: { data },
});

export const setCustomizeStepData = (payload: any) => ({
  type: actionTypes.SET_CUSTOMIZE_STEP_DATA,
  payload,
});

const setDefaultTemplates = (templates: any) => ({
  type: actionTypes.SET_DEFAULT_TEMPLATES,
  payload: { templates },
});

export const setErrors = (errors: any) => ({
  type: actionTypes.SET_ERRORS,
  payload: { errors },
});

const setHasLPOrFoil = () => ({
  type: actionTypes.SET_HAS_LP_OR_FOIL,
});

export const setNamesStepData = (data: any) => ({
  type: actionTypes.SET_NAMES_STEP_DATA,
  payload: { data },
});

const setOrderNumber = (orderNumber: string) => ({
  type: actionTypes.SET_ORDER_NUMBER,
  payload: { orderNumber },
});

export const setShippingStepData = (data: any) => ({
  type: actionTypes.SET_SHIPPING_STEP_DATA,
  payload: { data },
});

const setSingleSampleAvailable = (singleSampleAvailable: boolean) => ({
  type: actionTypes.SET_SINGLE_SAMPLE_AVAILABLE,
  payload: { singleSampleAvailable },
});

export const setStepKey = (stepKey: string) => ({
  type: actionTypes.SET_STEP_KEY,
  payload: { stepKey },
});

export const setUploadcareImage = (
  activePreviewUUID: string,
  cardSuiteUUIDList: string[],
  uploadcareFile: any
) => ({
  type: actionTypes.SET_UPLOADCARE_IMAGE,
  payload: { activePreviewUUID, cardSuiteUUIDList, uploadcareFile },
});

export const shippingDataFetched = () => ({
  type: actionTypes.SHIPPING_DATA_FETCHED,
});

export const updateCustomizationsData = (
  cardSuiteUUID: string,
  customizations: Customizations
) => ({
  type: actionTypes.UPDATE_CUSTOMIZATIONS_DATA,
  payload: { cardSuiteUUID, customizations },
});

const updateTemplatesSet = (cardSuiteUUID: string, template: any) => ({
  type: actionTypes.UPDATE_TEMPLATES_SET,
  payload: { cardSuiteUUID, template },
});

// API calls
export const fetchAvailableSamplesCount = (cardType: string) => (dispatch: AppDispatch) => {
  if (!cardType) {
    return Promise.resolve(null);
  }

  return ApiService.get(`/web-api/v1/card-single-sample/availability/${cardType}`).then(data => {
    const singleSampleAvailable = data.order_placed < data.maximum_orders_allowed;

    dispatch(setSingleSampleAvailable(singleSampleAvailable));
  });
};

export const fetchCardSuiteDetails = (cardSuiteUUIDList: string[], isAdminView: boolean) => (
  dispatch: AppDispatch,
  getState: () => RootState
) => {
  return ApiService.post('/web-api/v1/card-catalog/suites/details', {
    suite_uuids: cardSuiteUUIDList,
    show_unlisted: !!isAdminView,
  }).then(suiteDetails => {
    const singleSampleFlowState = getFlowData(getState());
    const activePDPVariation = getActiveVariationOptionValues(getState());

    Object.values(suiteDetails).forEach((suite: any) => {
      if ((suite.letterpress || suite.foil) && !singleSampleFlowState.hasLPOrFoil) {
        dispatch(setHasLPOrFoil());
      }

      if (!suite.letterpress && !suite.foil && singleSampleFlowState.allLPOrFoil) {
        dispatch(setAllLPOrFoil());
      }
    });

    dispatch(setCardSuiteDetails(suiteDetails, activePDPVariation));
    dispatch(cardSuiteFulfilled(suiteDetails[0]));
  });
};

export const fetchDefaultTemplates = (
  cardSuiteUUIDList: string[],
  cardSuiteDetailsSet: any,
  substitutions: { [key: string]: string } | null
) => (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
  const variationUUIDs: { [key: string]: string } = {};
  const singleSampleFlowState = getFlowData(getState());

  cardSuiteUUIDList.forEach(uuid => {
    const cardSuiteDetails = cardSuiteDetailsSet[uuid];
    // get selected color unless card is letterpress or foil (then use default)
    const selectedColor =
      cardSuiteDetails.letterpress || cardSuiteDetails.foil
        ? cardSuiteDetails.mediums.paper.default_variation_options.color
        : singleSampleFlowState.customizationsSet[uuid].color;
    const matched = matchColorToCardVariation(cardSuiteDetails, selectedColor);

    variationUUIDs[matched.uuid] = uuid;
  });

  return ApiService.post('/web-api/v1/card-template/preview/by-variations', {
    variation_uuids: Object.keys(variationUUIDs),
    substitutions: substitutions || {},
  }).then(templates => {
    const templatesBySuiteUUID: { [key: string]: any } = {};

    Object.entries(templates).forEach(template => {
      templatesBySuiteUUID[variationUUIDs[template[0]]] = template[1];
    });

    dispatch(setDefaultTemplates(templatesBySuiteUUID));
  });
};

export const fetchTemplate = (
  cardSuiteUUID: string,
  cardSuiteDetails: any,
  color: string,
  substitutions: { [key: string]: string } | null
) => (dispatch: AppDispatch) => {
  if (!cardSuiteDetails || !color) {
    return Promise.resolve();
  }

  const matched = matchColorToCardVariation(cardSuiteDetails, color);

  return ApiService.post('/web-api/v1/card-template/preview/by-variations', {
    variation_uuids: [matched.uuid],
    substitutions: substitutions || {},
  }).then(template => dispatch(updateTemplatesSet(cardSuiteUUID, template[matched.uuid])));
};

export const fetchUserShippingAddress = (userContext: any) => (dispatch: AppDispatch) => {
  const weddingDetails = extractUserWeddingDetails(userContext);
  const slug = weddingDetails && weddingDetails.slug;

  if (!slug) {
    Promise.resolve();
  }

  return ApiService.get(`/web-api/v1/card-single-sample/prefill-content/${slug}`).then(
    locationData => {
      if (locationData && locationData.shippingAddress) {
        const shipping = locationData.shippingAddress;

        dispatch(
          setShippingStepData({
            address: shipping.street_address_1,
            apt: shipping.street_address_2,
            city: shipping.city,
            state: shipping.state_province_region,
            zip: shipping.postal_code,
          })
        );
      }

      dispatch(shippingDataFetched());
    }
  );
};

export const submitSingleSampleForm = (
  cardSuiteUUIDList: string[],
  isGuest: boolean,
  userContext: any,
  trackingParams: Record<string, unknown>
) => (dispatch: AppDispatch, getState: () => RootState) => {
  const singleSampleFlowState = getFlowData(getState());

  const {
    account,
    allLPOrFoil,
    cardSuiteDetailsSet,
    cardType,
    customizationsSet,
    errors,
    names,
    shipping,
  } = singleSampleFlowState;

  let accountPromise;

  if (isGuest) {
    accountPromise = createAccount({
      accountDetails: account,
      currentErrors: errors,
      weddingDetails: names,
      shouldTrackOnboarding: false,
      isGuest,
    }).then((result: any): any => {
      if (result.errors) {
        dispatch(setErrors(result.errors));

        return result.errors.weddingDetails
          ? dispatch(setStepKey(STEPS.NAMES))
          : dispatch(setStepKey(STEPS.SHIPPING));
      }

      return dispatch(getUserContextAction());
    });
  } else if (!userContext.has_invitation_account) {
    accountPromise = createInvitationsAccount().then(() => ({}));
  } else {
    accountPromise = Promise.resolve({});
  }

  return accountPromise
    .then((result: any): any => {
      if (result.errors) {
        dispatch(setErrors(result.errors));

        return result.errors.weddingDetails
          ? dispatch(setStepKey(STEPS.NAMES))
          : dispatch(setStepKey(STEPS.SHIPPING));
      }

      const photoApiCalls = cardSuiteUUIDList.map(uuid => {
        const { uploadcareFile } = customizationsSet[uuid];

        if (uploadcareFile) {
          return copyUploadcareFileToZola(uploadcareFile).then((zolaFile: any) => ({
            ...result,
            suiteUUID: uuid,
            photoUUID: zolaFile.uuid,
          }));
        }

        return { ...result, suiteUUID: uuid };
      });

      return Promise.all(photoApiCalls).then(results => ({
        ...result,
        photoUUIDSet: _fromPairs(results.map(r => [r.suiteUUID, r.photoUUID])),
      }));
    })
    .then((result: any) => {
      if (result.errors) {
        dispatch(setErrors(errors));

        return result.errors.weddingDetails
          ? dispatch(setStepKey(STEPS.NAMES))
          : dispatch(setStepKey(STEPS.SHIPPING));
      }

      const { photoUUIDSet } = result;

      const coupleNames = extractCoupleNames(names);

      if (!coupleNames) {
        dispatch(
          setErrors({
            weddingDetails: {
              field: {
                fullName: 'Please make sure you enter your full name',
                partnerFullName: "Please make sure you enter your partner's full name",
              },
            },
          })
        );

        return dispatch(setStepKey(STEPS.NAMES));
      }

      const substitutions = allLPOrFoil ? {} : createSubstitutions(names);

      const projects = cardSuiteUUIDList.map(uuid => {
        const { variation } = customizationsSet[uuid];

        const activeVariationUUID = variation.uuid;

        return {
          variation_uuid: activeVariationUUID,
          suite_uuid: uuid,
          photo_uuid: photoUUIDSet[uuid],
          sku_object_id: !getIsSampleCustomizable(cardSuiteDetailsSet[uuid])
            ? cardSuiteDetailsSet[uuid].single_sample_sku
            : null,
        };
      });

      const cardInfo = {
        projects,
        wedding_details: allLPOrFoil
          ? {}
          : {
              ...coupleNames,
              event_date: names.date,
              ...substitutions,
            },
      };

      const body = {
        ...cardInfo,
        card_type: cardType,
        address_validation: {
          street_line_1: shipping.address,
          street_line_2: shipping.apt,
          city: shipping.city,
          state_province_region: shipping.state,
          postal_code: shipping.zip,
          country_code: 'USA',
        },
      };

      return ApiService.post('/web-api/v1/card-single-sample/order', body).then(data => {
        if (!data.addressValidated) {
          dispatch(
            setErrors({
              shipping: {
                field: {
                  address: 'Please check the address',
                  city: 'Please check the city',
                  state: 'Please check the state',
                  zipCode: 'Please check zip code',
                },
              },
            })
          );

          return dispatch(setStepKey(STEPS.SHIPPING));
        }

        if (!data.checkedOut || !data.orderNumber) {
          dispatch(
            setErrors({
              shipping: {
                form: "Something went wrong. Let's try that again.",
              },
            })
          );

          return dispatch(setStepKey(STEPS.SHIPPING));
        }

        return dispatch(setOrderNumber(data.orderNumber));
      });
    })
    .then((result: any): any => {
      if (result.errors) {
        dispatch(setErrors(result.errors));

        return result.errors.weddingDetails
          ? dispatch(setStepKey(STEPS.NAMES))
          : dispatch(setStepKey(STEPS.SHIPPING));
      }

      trackEvent('Flow Completed', {
        ...trackingParams,
        is_error: false,
      });

      return dispatch(getUserContextAction()).then(data => {
        const order_id = result && result.orderDetails && result.orderDetails.id;
        trackGoogleTagManagerEvent('Paper Free Sample', {
          order_id,
          revenue: 0,
          email: data.email,
        });
      });
    });
};
